import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { TeamExtendedProfile } from 'src/replicant/team/team.schema';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import { parseAmount } from 'src/lib/utils';
import { MAX_MEMBERS, TeamMemberData } from 'src/replicant/team/team.ruleset';
import { AsyncScroll } from 'src/game/components/shared/AsyncScroll';
import { TeamMemberInfoEntry } from '../components/TeamMemberInfoEntry';
import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { breakJapaneseText, isJapanese } from 'src/replicant/utils/string';
import { getCurrentTeamCompetition } from '../team.sequence';
import { track } from 'src/lib/analytics/utils';

import { AssetGroup } from '@play-co/timestep-core';

const ui = uiConfig.buruburu.care.confirmation;

// Popup will return one of these results, depend on which button user clicked
export type PopupResult = 'close' | 'leave' | 'join';

export default class PopupTeamInfo extends PopupBasic {
  private close: (result: PopupResult) => void;
  private team!: { update: (team: TeamExtendedProfile) => void };
  private members!: {
    update: (
      team: TeamExtendedProfile,
      getMembers: () => Promise<TeamMemberData[]>,
    ) => void;
  };
  private teamProfile!: TeamExtendedProfile;

  constructor(opts: { superview: View; close: (result: PopupResult) => void }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close('close');
      },
      ...ui.popup,
      titleIcon: {
        image: 'assets/ui/match2/team/info_icon.png',
        width: 55,
        height: 44,
        offsetX: 65,
      },
      width: 692,
      height: 980,
      skipBackground: true,
      skipMessage: true,
      darkerBg: true,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.INFO_POPUP._,
      },
    });

    this.title.updateOpts({ size: 34 });
    this.buttonClose.updateOpts({ x: 692 - this.buttonClose.style.width / 2 });

    const info = { width: 640, height: 520 };

    // Background image
    new ImageScaleView({
      superview: this.box,
      ...uiConfig.popups.buruburu,
      width: info.width,
      height: info.height,
      x: (this.box.style.width - info.width) * 0.5,
      y: 20,
    });

    // Team information part
    this.team = this.createTeamInfo(this.box, info);
    // Members list
    this.members = this.createMembers(this.box, info);

    // Header image
    new ImageView({
      superview: this.box,
      image: 'assets/ui/match2/team/team_awning.png',
      width: 692,
      height: 106,
      offsetX: -(this.box.style.width - info.width) * 0.5,
      x: (this.box.style.width - info.width) * 0.5,
    });

    this.close = opts.close;
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/team/info_icon.png',
      'assets/ui/match2/team/team_awning.png',
      'assets/ui/match2/team/frames/frame_tc_create.png',
      'assets/ui/match2/team/frames/members_counter_frame.png',
      'assets/ui/match2/team/memebrs_counter_icon.png',
      'assets/ui/match2/team/frames/panel_list_beige_inset.png',
    ]);
  }

  /**
   * Create team information
   *
   * return { update: (team: TeamExtendedProfile) => void }
   */
  private createTeamInfo(
    superview: View,
    size: { width: number; height: number },
  ) {
    const { width, height } = size;

    // Layout start from here
    const box = new FlexBoxView({
      tag: 'teamInfo',
      superview,
      x: (superview.style.width - width) * 0.5,
      y: 0,
      width,
      height,
      infinite: true,
      flex: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    const card = this.createTeamInfoCard(box);
    const buttons = this.createButtons(box);

    return {
      update: (team: TeamExtendedProfile) => {
        buttons.update(team);
        card.update(team);
      },
    };
  }

  private createTeamInfoCard(superview: View) {
    const layout = new FlexBoxView({
      tag: 'temInformationCard',
      superview: superview,
      offsetY: 40,
      width: 577,
      height: 358,
    });

    const background = new ImageScaleView({
      superview: layout,
      image: 'assets/ui/match2/team/frames/frame_tc_create.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 49, right: 49 },
        vertical: { top: 49, bottom: 49 },
      },
      width: layout.style.width,
      height: layout.style.height,
    });

    const avatar = new Avatar({
      superview: background,
      width: 100,
      height: 100,
      centerOnOrigin: false,
    });

    avatar.updateOpts({
      offsetX: 20,
      offsetY: 20,
    });

    const name = new LangBitmapFontTextView({
      superview: background,
      x: 140,
      y: 20,
      align: 'left',
      verticalAlign: 'center',
      size: 38,
      font: 'Body',
      width: 278,
      height: 40,
      color: '#F24463',
      localeText: () => 'Unknown Team Name',
    });

    const description = new LangBitmapFontTextView({
      superview: background,
      x: name.style.x,
      y: name.style.y + name.style.height + 10,
      align: 'left',
      verticalAlign: 'center',
      size: 22,
      font: 'Body',
      width: 380,
      height: 78,
      color: '#BF8147',
      wordWrap: true,
      localeText: () => 'Unknown Team Description',
    });

    const information = this.createTeamInformationItems(background);
    const membersCounter = this.createMembersCounter(layout);

    return {
      update: (team: TeamExtendedProfile) => {
        avatar.updateImage(team.icon);
        name.localeText = () => team.name;
        description.localeText = () =>
          isJapanese(team.description)
            ? (breakJapaneseText(team.description) ?? '')
            : team.description;
        information.update(team);
        membersCounter.update(team);
      },
    };
  }

  /**
   * Create team information items layout
   *
   * return { update: (team: TeamExtendedProfile) => void }
   */
  private createTeamInformationItems(superview: View) {
    const layout = new FlexBoxView({
      tag: 'temInformationItems',
      superview,
      x: (superview.style.width - 560) * 0.5,
      y: 180,
      backgroundColor: '#FFFFF7',
      width: 560,
      height: 94,
      flex: {
        flexDirection: 'row',
      },
    });

    const score = this.createTeamInformationItem(
      layout,
      (team) => team.score,
      i18n('match2.team.info_popup.teamScore'),
    );

    const helps = this.createTeamInformationItem(
      layout,
      (team) => team.helps,
      i18n('match2.team.info_popup.weeklyHelps'),
    );

    const level = this.createTeamInformationItem(
      layout,
      (team) => team.levelRequired,
      i18n('match2.team.info_popup.requiredLvl'),
    );

    return {
      update: (team: TeamExtendedProfile) => {
        score.update(team);
        helps.update(team);
        level.update(team);
      },
    };
  }

  /**
   * Create team information item: label on top and value on bottom
   *
   * return { update: (team: TeamExtendedProfile) => void }
   */
  private createTeamInformationItem(
    superview: View,
    getValue: (team: TeamExtendedProfile) => number,
    label: string,
  ) {
    const layout = new FlexBoxView({
      tag: 'temInformationItem',
      superview: superview,
      backgroundColor: '#FFFFF7',
      width: 192,
      height: superview.style.height,
      flex: {
        flexDirection: 'column',
        justifyContent: 'space-around',
      },
    });

    new LangBitmapFontTextView({
      superview: layout,
      offsetY: -5,
      align: 'center',
      verticalAlign: 'bottom',
      size: 20,
      font: 'Body',
      width: layout.style.width,
      height: layout.style.height * 0.5,
      color: '#662420',
      localeText: () => label,
    });

    const value = new LangBitmapFontTextView({
      superview: layout,
      align: 'center',
      verticalAlign: 'top',
      size: 30,
      font: 'Body',
      width: layout.style.width,
      height: layout.style.height * 0.5,
      color: '#F24463',
      localeText: () => '-',
    });

    return {
      update: (team: TeamExtendedProfile) => {
        const data = getValue(team);
        value.localeText = () => (data ? parseAmount(data) : '-');
      },
    };
  }

  /**
   * Create buttons, depend on if user are member or not we will show LEAVE or JOIN button
   *
   * return { update: (team: TeamExtendedProfile) => void }
   */
  private createButtons(superview: View) {
    const buttonsLayout = new FlexBoxView({
      tag: 'buttonsLayout',
      superview,
      width: 550,
      height: 90,
      flex: {
        bottom: 0,
        position: 'absolute',
        flexDirection: 'row',
        justifyContent: 'space-around',
      },
    });

    const leave = new ButtonScaleViewWithText({
      ...ui.backButton,
      superview: buttonsLayout,
      localeText: () => i18n('match2.team.info_popup.leave'),
      onClick: async () => {
        sounds.sfx.play('cancel');

        const leave = await openPopupPromise(
          'popupTeamLeave',
          this.teamProfile,
        );

        if (!leave) return;

        // Start fetching competition data before leaving the team.
        const competitionPromise =
          getCurrentTeamCompetition()?.get() ?? Promise.resolve(null);

        // Leave team.
        await StateObserver.invoke.leaveTeam();

        // Track once we have all the data.
        competitionPromise.then((competition) => {
          track('TeamLeave', {
            id: this.teamProfile.id,
            memberCount: this.teamProfile.memberCount,
            canJoinCurrentEvent: competition?.canJoin,
            joinedCurrentEvent: competition?.joined,
            score: this.teamProfile.score,
            helps: this.teamProfile.helps,
          });
        });

        this.close('leave');
      },
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.INFO_POPUP.LEAVE,
      },
    });

    const join = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: buttonsLayout,
      localeText: () => i18n('match2.team.info_popup.join'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.close('join');
      },
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.INFO_POPUP.JOIN,
      },
    });

    return {
      update: (team: TeamExtendedProfile) => {
        leave.style.visible = team.member;
        join.style.visible = !team.member;

        const shouldDisableButton = team.memberCount >= MAX_MEMBERS; // Team is full
        join.setDisabled(shouldDisableButton);
      },
    };
  }

  /**
   * Create team members counter UI
   *
   * return { update: (team: TeamExtendedProfile) => void }
   */
  private createMembersCounter(superview: View) {
    const layout = new FlexBoxImageScaleView({
      tag: 'membersCounter',
      superview,
      image: 'assets/ui/match2/team/frames/members_counter_frame.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 70, right: 70 },
        vertical: { top: 32, bottom: 32 },
      },
      width: 143,
      height: 66,
      flex: {
        top: 0,
        right: 5,
        position: 'absolute',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    new ImageView({
      superview: layout,
      image: 'assets/ui/match2/team/memebrs_counter_icon.png',
      width: 31,
      height: 30,
    });

    const counter = new LangBitmapFontTextView({
      superview: layout,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Body',
      width: 87,
      height: 42,
      color: '#C44433',
      localeText: () => `0/${MAX_MEMBERS}`,
    });

    return {
      update: (team: TeamExtendedProfile) => {
        counter.localeText = () => `${team.memberCount}/${MAX_MEMBERS}`;
      },
    };
  }

  /**
   * Create members list UI
   *
   * return { update: (members: TeamMemberData[]) => void }
   */
  private createMembers(
    superview: View,
    size: { width: number; height: number },
  ) {
    const { width, height } = size;

    const layout = new FlexBoxImageScaleView({
      tag: 'membersList',
      ...uiConfig.popups.buruburu,
      superview,
      x: (superview.style.width - width) * 0.5,
      y: height,
      offsetY: 12,
      width: width,
      height: height,
      flex: {
        bottom: 0,
        position: 'absolute',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    new FlexBoxImageScaleView({
      tag: 'membersListBg',
      superview: layout,
      image: 'assets/ui/match2/team/frames/panel_list_beige_inset.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 39, right: 39 },
        vertical: { top: 39, bottom: 39 },
      },
      width: 590,
      height: 480,
      flex: {
        top: 35,
        position: 'absolute',
      },
    });

    const scroll = new AsyncScroll<TeamMemberData, TeamMemberInfoEntry>({
      superview: layout,
      rect: {
        x: 0,
        y: 0,
        width: 582,
        height: 478,
      },
      createItem: (superview, _, data) => {
        const entry = new TeamMemberInfoEntry({
          superview,
          width: 553,
          height: 75,
        });
        entry.style.x = (superview.style.width - entry.style.width) * 0.5;
        if ('loading' in data) return entry;
        entry.setup(data);
        return entry;
      },
      recycle: true,
      setData: (_, data, view) => {
        if ('loading' in data) return;
        view.setup(data);
      },
    });

    getFlexNode(scroll.getView()).updateStyle({
      position: 'absolute',
      top: 35,
    });

    return {
      update: (
        team: TeamExtendedProfile,
        getMembers: () => Promise<TeamMemberData[]>,
      ) => {
        const show = !team.member;
        layout.style.visible = show;
        if (show) {
          scroll.setDataProvider(async () => getMembers(), {
            feature: Feature.TEAM._,
            subFeature: Feature.TEAM.GET_TEAM_MEMBERS,
          });
        }
      },
    };
  }

  /**
   * Init view
   */
  public init(opts: { team: TeamExtendedProfile }): void {
    super.init({
      title: i18n('match2.team.info_popup.title'),
    });

    this.teamProfile = opts.team;
    this.team.update(opts.team);
    this.members.update(opts.team, () =>
      StateObserver.replicant.asyncGetters.getTeamMembers({
        teamId: opts.team.id,
      }),
    );
  }
}
